import { QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Toaster } from 'sonner'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { AuthProvider } from '@/features/auth/auth-context'
import { LocaleProvider } from '@/i18n/locale-context'
import { queryClient } from '@/lib/query-client'

/**
 * Application providers, outermost first.
 *
 * Order matters and is not arbitrary:
 *   ErrorBoundary  — must wrap everything, including provider failures.
 *   QueryClient    — Auth clears the cache on sign-out, so it must exist
 *                    before Auth mounts.
 *   Auth           — everything below it may read the session.
 *   Locale         — reads the session to load the stored preference,
 *                    and uses the query client to refresh the profile.
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LocaleProvider>
            {children}
            <Toaster position="bottom-center" closeButton richColors />
          </LocaleProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
